import { Method } from "axios";
import { ActionKeyPair, IService } from "@/shared/hooks/useCustomQuery";
import api from "@/shared/api/api";
import { PresetSummary } from "../model/analytics.types";

export interface GetPresetsSummaryParams {
  start: string;
  end: string;
}

export interface GetPresetsUsageByPeriodParams {
  start: string;
  end: string;
}

export interface PresetsUsageByPeriodResponse {
  period: {
    start: string;
    end: string;
  };
  data: Array<{
    month: string;
    paidUses: number;
    freeUses: number;
  }>;
}

export type GroupByType = 'pack' | 'creator';
export type SortByType = 'createdAt' | 'growth' | 'totalUses';
export type OrderType = 'asc' | 'desc';

export interface GetPacksAnalyticsParams {
  page?: number;
  limit?: number;
  start?: string;
  end?: string;
  groupBy?: GroupByType;
  sortBy?: SortByType;
  order?: OrderType;
}


export interface PackAnalyticsGroupedItem {
  groupId: string;
  groupName: string;
  groupType: GroupByType;
  createdAt: string;
  freeUse: number;
  paidUse: number;
  totalUses: number;
  growthRate: number;
}


export interface PacksAnalyticsResponse<T = PackAnalyticsGroupedItem> {
  page: number;
  limit: number;
  totalPage: number;
  data: T[];
}

export default class AnalyticsService implements IService {
  [key: string]: ActionKeyPair<any, any>;

  getPresetsSummary: ActionKeyPair<GetPresetsSummaryParams, PresetSummary> = {
    key: "get-presets-summary",
    actionBuilder: (params: GetPresetsSummaryParams) => async (): Promise<PresetSummary> => {
      let { start, end } = params;
      if (!end) {
        end = new Date().toISOString().split("T")[0]
      }
      if (!start) {
        start = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split("T")[0].slice(0, 7);
      }
      const url = `/v1/analytics/presets/summary?start=${start}&end=${end}`;
      const response = await api<PresetSummary>({
        url,
        method: "GET" as Method,
      });
      return response;
    },
  };

  getPresetsUsageByPeriod: ActionKeyPair<
    GetPresetsUsageByPeriodParams,
    PresetsUsageByPeriodResponse
  > = {
      key: "get-presets-usage-by-period",
      actionBuilder: (params: GetPresetsUsageByPeriodParams) => async (): Promise<PresetsUsageByPeriodResponse> => {
        const { start, end } = params;
        const url = `/v1/analytics/presets/usage?start=${start}&end=${end}`;
        
        const response = await api<PresetsUsageByPeriodResponse>({
          url,
          method: "GET" as Method,
        });
        return response;
      },
    };

  getPacksAnalytics: ActionKeyPair<
    GetPacksAnalyticsParams,
    PacksAnalyticsResponse
  > = {
      key: "get-packs-analytics",
      actionBuilder: (params: GetPacksAnalyticsParams) => async (): Promise<PacksAnalyticsResponse> => {
        let { start, end } = params;
        if (!end) {
          end = new Date().toISOString().split("T")[0];
        }
        if (!start) {
          start = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split("T")[0];
        }
        const urlParams = new URLSearchParams();
        urlParams.set("page", params.page?.toString() || "1");
        urlParams.set("limit", params.limit?.toString() || "10");
        urlParams.set("start", start!);
        urlParams.set("end", end!);
        if (params.groupBy) {
          urlParams.set("groupBy", params.groupBy);
        }
        if (params.sortBy) {
          urlParams.set("sortBy", params.sortBy);
        }
        if (params.order) {
          urlParams.set("order", params.order);
        }
        const url = `/v1/analytics/packs?${urlParams.toString()}`;
        const response = await api<PacksAnalyticsResponse>({
          url,
          method: "GET" as Method,
        });
        return response;
      },
    };
  private static getDefaultStartEndDatesForPresetsUsage() {
    return {
      start: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split("T")[0],
      end: new Date().toISOString().split("T")[0]
    };
  }

  static Instance = new AnalyticsService();
}
